import { Button, Separator, Skeleton } from '@1trading/react';
import { CircleStop, Pause, Play, Square } from 'lucide-react';
import { useSetMakerState, useSetTakerState } from '@/hooks/queries/market-maker/use-market-maker';
import type { MarketStatus } from '@/services/api/market-maker';
import { MarketCard } from './market-card';

const LOADING_CARD_COUNT = 3;

const LoadingPanel = () => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
      <Skeleton className="h-44 w-full" key={index} />
    ))}
  </div>
);
LoadingPanel.displayName = 'LoadingPanel';

interface MarketsSectionProps {
  markets: MarketStatus[];
  /** First load only — the 10 s poll ticks must not flash the loading panel */
  isPending: boolean;
}

// No confirmation dialogs anywhere — every control fires immediately.
export const MarketsSection = ({ markets, isPending }: MarketsSectionProps) => {
  const makerMutation = useSetMakerState();
  const takerMutation = useSetTakerState();

  if (isPending && markets.length === 0) return <LoadingPanel />;
  if (markets.length === 0) return null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Markets</h2>
        <div className="flex items-center gap-2">
          <Button className="gap-1.5 text-emerald-500" onClick={() => makerMutation.mutate({ state: 'start' })} size="sm" type="button" variant="outline">
            <Play className="size-3.5" />
            Start All
          </Button>
          <Button className="gap-1.5 text-amber-500" onClick={() => makerMutation.mutate({ state: 'pause' })} size="sm" type="button" variant="outline">
            <Pause className="size-3.5" />
            Pause All
          </Button>
          <Button className="gap-1.5 text-red-500" onClick={() => makerMutation.mutate({ state: 'stop' })} size="sm" type="button" variant="outline">
            <Square className="size-3.5" />
            Stop All
          </Button>
          <Separator className="h-6" orientation="vertical" />
          {/* Deliberately no global Start/Pause Takers — Stop is the only global taker action */}
          <Button className="gap-1.5 text-red-500" onClick={() => takerMutation.mutate({ state: 'stop' })} size="sm" type="button" variant="outline">
            <CircleStop className="size-3.5" />
            Stop Takers
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {markets.map((market) => (
          <MarketCard
            key={market.market}
            market={market}
            onMakerAction={(state) => makerMutation.mutate({ state, market: market.market })}
            onTakerAction={(state) => takerMutation.mutate({ state, market: market.market })}
          />
        ))}
      </div>
    </section>
  );
};
MarketsSection.displayName = 'MarketsSection';
